import useAbortController from '@hooks/useAbortController';
import { closeDeliveryPicker, openDeliveryPicker, selectDeliveryPicker } from '@slices/deliveryPickerSlice';
import { trackDeliveryConfirmedTime } from '@helpers/analyticsHelpers/trackDeliveryWidget';
import type { AddressData, AddToCartForm, AxfoodCartProductViewModel, AxfoodCartViewModel } from '@occ/api-client';
import type { AxfoodStoreInfoViewModel } from '@occ/api-client';
import getPickUnit from '@helpers/getPickUnit';
import {
  addToCart,
  setCartContactInfo,
  clearCart,
  setSlotInCart,
  putOrderInCart,
  replaceEko,
  replaceAll,
} from '@api/interfaces/cartApi';
import { setDiscountTooltip, setInfoTooltip } from '@slices/miniCartSlice';
import trackChangeOrderModal from '@helpers/analyticsHelpers/trackChangeOrderModal';
import { setChangeOrderModalVisibility } from '@slices/modalSlice';
import paths from '@constants/paths';
import trackRemoveFromCart from '@helpers/analyticsHelpers/trackRemoveFromCart';
import useCart from '@hooks/useCart';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import { useRouter } from 'next/navigation';
import { setIsUpdatingCart } from '@slices/cartUpdatingSlice';
import useLocalStorage from '@hooks/useLocalStorage';
import config from '@config';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import useStore from './useStore';
import useCustomer from '@hooks/useCustomer';
import { isAtLowestHistoricalPrice } from '@helpers/productPromotion';

type ReplaceableProduct = { [key: string]: boolean };
const getReplaceableProducts = (products: AxfoodCartProductViewModel[]): ReplaceableProduct => {
  return products.reduce<ReplaceableProduct>((acc, current) => {
    if (current.canBeReplaced) {
      acc[current.code] = current.replacement;
    }
    return acc;
  }, {});
};
const makeProductWithNoReplacementFlag = (allProducts: AxfoodCartProductViewModel[], product: AddToCartForm) => {
  const prevReplaceableProducts = getReplaceableProducts(allProducts); // [product,...] -> [{code: replacement},...]
  const numberOfReplacementKeys = Object.keys(prevReplaceableProducts).filter((replacement: string) => {
    return prevReplaceableProducts[replacement];
  }).length;
  const isReplaceSome = Object.keys(prevReplaceableProducts).length > numberOfReplacementKeys;

  // product: CartProduct => {...product, noReplacementFlag: true|false}
  return {
    ...product,
    noReplacementFlag:
      prevReplaceableProducts[product.productCodePost] !== undefined
        ? !prevReplaceableProducts[product.productCodePost]
        : isReplaceSome,
  };
};

const useCartActions = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const deliveryPicker = useAppSelector(selectDeliveryPicker);
  const { cart, isCartEmpty, refreshCart } = useCart();
  const { getSignal } = useAbortController();
  const checkCartConflict = useCheckCartConflict({ checkStock: false });
  const [deliveryModeReminderDone, setDeliveryModeReminderDone] = useLocalStorage(
    config.LOCAL_STORAGE_SELECTORS.deliveryModeReminderDone,
    false
  );
  const { store } = useStore();
  const { customer } = useCustomer();
  const isLoggedIn = !customer?.isAnonymous;

  const updateSlotInCart = async (
    slot: string,
    isTmsSlot: boolean,
    tmsDeliveryWindowReference: any,
    homeDelivery: boolean,
    isCheckout: boolean
  ) => {
    try {
      dispatch(setIsUpdatingCart(true));
      await checkCartConflict({ slotCode: slot });
      const cartResp = await setSlotInCart(slot, isTmsSlot, tmsDeliveryWindowReference, getSignal());

      await refreshCart(cartResp.data);
      // User closing the delivery picker isn’t tracked here.
      dispatch(closeDeliveryPicker({ proceedToCheckout: deliveryPicker.proceedToCheckout }));
      trackDeliveryConfirmedTime(homeDelivery, isCheckout, store?.storeId || '');
      dispatch(setIsUpdatingCart(false));
      return Promise.resolve(cartResp);
    } catch (e) {
      dispatch(setIsUpdatingCart(false));
      return Promise.reject(e);
    }
  };

  const addOrderToCart = async (orderID: string) => {
    dispatch(setIsUpdatingCart(true));
    const cart = await putOrderInCart(orderID);

    await refreshCart(cart.data);

    dispatch(setIsUpdatingCart(false));
  };

  const updateCartContactInfo = async (contactInfo: AddressData) => {
    try {
      dispatch(setIsUpdatingCart(true));
      const cartResp = await setCartContactInfo(contactInfo);

      await refreshCart(cartResp);
      dispatch(setIsUpdatingCart(false));
      return Promise.resolve(cartResp);
    } catch (e) {
      dispatch(setIsUpdatingCart(false));
      return Promise.reject(e);
    }
  };

  const postToCart = async (
    products: Array<{
      product: AxfoodCartProductViewModel;
      newQuantity: number;
      prevQuantity: number;
    }>,
    store?: AxfoodStoreInfoViewModel
  ) => {
    dispatch(setIsUpdatingCart(true));

    try {
      const activelySelected = !!store?.activelySelected;
      const cartProducts: AddToCartForm[] = products.map(({ product, newQuantity }) => {
        const hideDiscountToolTip = !isAtLowestHistoricalPrice(product);

        return makeProductWithNoReplacementFlag(cart?.products || [], {
          productCodePost: product.code,
          qty: newQuantity,
          pickUnit: getPickUnit(product?.productBasketType?.code),
          hideDiscountToolTip,
        });
      });

      const wasEmpty = Boolean(isCartEmpty);

      const data = await addToCart(cartProducts);

      // PIL Discount value
      if (data.discountToolTip && data.discountToolTip > 0) {
        dispatch(setDiscountTooltip({ value: data.discountToolTip, show: true }));
      }

      await refreshCart(data);
      dispatch(setIsUpdatingCart(false));

      products.forEach(({ product, newQuantity, prevQuantity }) => {
        if (product.addToCartMessage && newQuantity > prevQuantity && prevQuantity === 0) {
          dispatch(setInfoTooltip({ text: product.addToCartMessage, show: true }));
        } else if (product.nicotineMedicalProduct && newQuantity > prevQuantity) {
          dispatch(setInfoTooltip({ text: 'nicotine', show: true }));
        } else if (product.tobaccoProduct && newQuantity > prevQuantity) {
          dispatch(setInfoTooltip({ text: 'tobacco', show: true }));
        }
      });

      const shouldDispatchDeliveryPicker =
        data?.products?.length === 1 && !activelySelected && !deliveryModeReminderDone;
      if (shouldDispatchDeliveryPicker) {
        dispatch(openDeliveryPicker({ trackingSource: 'after_add_to_cart' }));
        setDeliveryModeReminderDone(true);
      }

      return { cartCode: data.code, wasEmpty, isLoggedIn };
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const cancelChangeOrder = async (cart?: AxfoodCartViewModel) => {
    trackChangeOrderModal('cancel_change_order_modal', 'confirm');
    const newCart = await clearCart(true);
    await refreshCart(newCart.data);
    dispatch(setChangeOrderModalVisibility(false));
    router.replace(`${paths.ACCOUNT_ORDERS}/${cart?.orderReference}`);
    trackRemoveFromCart(cart?.products ?? [], 'cancel_change_order', cart?.code || '', isLoggedIn);
  };

  const postReplaceEko = async (replace: boolean) => {
    dispatch(setIsUpdatingCart(true));
    try {
      const resp = await replaceEko(replace);
      dispatch(setIsUpdatingCart(false));
      await refreshCart(resp.data);
      return Promise.resolve(resp);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const postReplaceAll = async (replace: boolean[]) => {
    dispatch(setIsUpdatingCart(true));
    try {
      const resp = await replaceAll(replace);
      dispatch(setIsUpdatingCart(false));
      await refreshCart(resp);
      return Promise.resolve(resp);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  return {
    addOrderToCart,
    cancelChangeOrder,
    postReplaceAll,
    postReplaceEko,
    postToCart,
    updateCartContactInfo,
    updateSlotInCart,
  };
};

export default useCartActions;
